import React, { useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { Shift } from 'domain/Shift';
import { Spot } from 'domain/Spot';
import { RosterState } from 'domain/RosterState';
import { Holiday } from 'domain/Holiday';

import CalendarHeader from './CalendarHeader';
import RowColumn, { RowItem } from './EmployeeColumn';
import ScheduleGrid from './ScheduleGrid';
import { CellAssignment } from './ScheduleCell';
import { formatISODate } from 'util/dates';

/* =========================
 * Props
 * ========================= */

interface Props {
  spots: Spot[];
  shifts: Shift[];
  startDate: Date;
  endDate: Date;
  rosterState: RosterState;
  isCopyModeActive: boolean;

  getShiftStyle: (shift: Shift) => any;
  onAddShift: (spot: Spot, start: Date, end: Date) => void;
  onUpdateShift: (shift: Shift, spot: Spot, start: Date, end: Date) => void;
  onEditShift: (shift: Shift) => void;
  onDeleteShift: (shift: Shift) => void;
  onCopyShift: (shift: Shift) => void;
  onPasteShift: (spot: Spot, date: Date) => void;
  holidays: Holiday[];
}

/* =========================
 * Component
 * ========================= */

const ShiftRosterGrid: React.FC<Props> = React.memo(
  ({ 
    spots,
    shifts,
    startDate,
    endDate,
    rosterState,
    getShiftStyle,
    isCopyModeActive,
    onAddShift,
    onUpdateShift,
    onEditShift,
    onDeleteShift,
    onCopyShift,
    onPasteShift,
    holidays,
  }) => {
    /* -------------------------
     * Refs para scroll sync
     * ------------------------- */
    const headerScrollRef = useRef<HTMLDivElement>(null);
    const gridScrollRef = useRef<HTMLDivElement>(null);

    const horizontalBarRef = useRef<HTMLDivElement>(null);
    const [horizontalContentWidth, setHorizontalContentWidth] = useState(0);

    const spotById = useMemo(() => {
      const map = new Map<number | string, Spot>();
      spots.forEach(s => map.set(s.id!, s));
      return map;
    }, [spots]);


    /* -------------------------
     * Days visibles
     * ------------------------- */
    const dates = useMemo(() => {
      const d: Date[] = [];
      let curr = moment(startDate);
      while (curr.isSameOrBefore(endDate, 'day')) {
        d.push(curr.toDate());
        curr = curr.add(1, 'day');
      }
      return d;
    }, [startDate, endDate]);

    const columnCount = dates.length;

    /* -------------------------
     * Filas (SPOTS)
     * ------------------------- */
    const spotRows = useMemo(() => {
      return spots.map((s) => ({
        id: s.id!,
        name: s.name,
        spot: s, // 🔑 guardar el objeto completo
      }));
    }, [spots]);

    /* -------------------------
     * Assignments map
     * ------------------------- */
    const assignments = useMemo(() => {
      const map = new Map<string, CellAssignment[]>();

      

      shifts.forEach((shift) => {
        

        const spotId = shift.spot?.id ?? (shift as any).spotId;
        if (!spotId) return;

        const iso = formatISODate(shift.startDateTime);
        const key = `${spotId}__${iso}`;

        const violationLists = [
          shift.requiredSkillViolationList,
          shift.unavailableEmployeeViolationList,
          shift.shiftEmployeeConflictList,
          shift.rotationViolationPenaltyList,
          shift.contractMinutesViolationPenaltyList,
          shift.noBreakViolationList,
          shift.restOneDayViolationList,
          shift.balanceEmployeeViolationList,
          shift.differentEmployeeDayShiftsViolationList,
          shift.bloquedViolationPenaltyList,
        ];

        const hasAnyViolation = violationLists.some(
          list => (list?.length ?? 0) > 0
        );

        const movedByHoliday =
          shift.originalScheduledDate &&
          moment(shift.originalScheduledDate).format('YYYY-MM-DD') !==
          moment(shift.startDateTime).format('YYYY-MM-DD');

        const color: CellAssignment['color'] =
          movedByHoliday
            ? 'orange'
            : !shift.employee
            ? 'yellow'
            : hasAnyViolation
            ? 'red'
            : 'gray';

        const item: CellAssignment = {
          id: shift.id!,
          shift,
          startTime: moment(shift.startDateTime).format('hh:mm A'),
          endTime: moment(shift.endDateTime).format('hh:mm A'),
          employeeName: shift.employee?.name,
          color,
        };

        const list = map.get(key) ?? [];
        list.push(item);
        map.set(key, list);
      });

      return map;
    }, [shifts]);

    
    const dateISOs = useMemo(() => dates.map(formatISODate), [dates]);
    const MIN_ROW_HEIGHT = 45;

    const CARD_H = 60;        // igual al CSS height
    const GAP = 6;            // igual al gap de .cellStack
    const CELL_PAD_Y = 12;    // schCell padding: 6px arriba + 6px abajo
    const EXTRA = 2;          // por bordes/rounding (margen de seguridad)

    const rowHeights = useMemo(() => {
      const map = new Map<number | string, number>();

      spotRows.forEach((row) => {
        let maxTurns = 0;

        dateISOs.forEach((iso) => {
          const key = `${row.id}__${iso}`;
          const count = assignments.get(key)?.length ?? 0;
          if (count > maxTurns) maxTurns = count;
        });

        const contentH =
          maxTurns === 0
            ? 0
            : (maxTurns * CARD_H) + ((maxTurns - 1) * GAP);

        const h = Math.max(MIN_ROW_HEIGHT, CELL_PAD_Y + contentH + EXTRA);
        map.set(row.id, h);
      });

      return map;
    }, [spotRows, dateISOs, assignments]);


    /* -------------------------
     * Scroll sync
     * ------------------------- */
    const syncScroll = (source: 'grid' | 'bar', left: number) => {
      if (source === 'bar' && gridScrollRef.current) {
        gridScrollRef.current.scrollLeft = left;
      }

      if (source === 'grid' && horizontalBarRef.current) {
        horizontalBarRef.current.scrollLeft = left;
      }

      if (headerScrollRef.current) {
        headerScrollRef.current.scrollLeft = left;
      }
    };

    useEffect(() => {
      const grid = gridScrollRef.current;
      const bar = horizontalBarRef.current;

      if (!grid || !bar) return;

      const updateWidth = () => {
        const gridMaxScroll = Math.max(
          0,
          grid.scrollWidth - grid.clientWidth
        );

        setHorizontalContentWidth(
          bar.clientWidth + gridMaxScroll
        );

        bar.scrollLeft = grid.scrollLeft;
      };

      updateWidth();

      const ResizeObserverClass = (window as any).ResizeObserver;

      const observer = ResizeObserverClass
        ? new ResizeObserverClass(updateWidth)
        : null;

      if (observer) {
        observer.observe(grid);
        observer.observe(bar);
      }

      window.addEventListener('resize', updateWidth);

      return () => {
        if (observer) observer.disconnect();
        window.removeEventListener('resize', updateWidth);
      };
    }, [spots, dates, shifts]);

    return (
      <>
        <div className="schPage">
          <div className='schScroller'>
            {/* HEADER SUPERIOR (días) */}
            <CalendarHeader
              dates={dates}
              columnCount={dates.length}
              headerScrollRef={headerScrollRef}
              holidays={holidays}
            />
             <div className="schHorizontalBar">
              <div className="schHorizontalBar__corner" />

              <div
                className="schHorizontalBar__viewport"
                ref={horizontalBarRef}
                onScroll={(e) =>
                  syncScroll('bar', e.currentTarget.scrollLeft)
                }
              >
                <div
                  style={{
                    width: horizontalContentWidth,
                    height: 1
                  }}
                />
              </div>
            </div>

            {/* CUERPO PRINCIPAL */}
            <div className="schMain">
              {/* COLUMNA EMPLEADOS */}
              <RowColumn
                rows={spotRows}
                rowHeights={rowHeights}
              />

              {/* GRID CALENDARIO */}
              <ScheduleGrid
                employees={spotRows}
                dates={dates}
                assignments={assignments}
                columnCount={dates.length}
                rowHeights={rowHeights}
                gridScrollRef={gridScrollRef}
                isCopyModeActive={isCopyModeActive}
                onGridScroll={(left) => syncScroll('grid', left)}
                

                onAddShift={(spotId, dateISO) => {
                  const spot = spotById.get(spotId);
                  if (!spot) return;

                  const date = moment(dateISO).toDate();

                  // 🔥 PRIORIDAD: modo copiar
                  if (isCopyModeActive) {
                    onPasteShift(spot, date);
                    return;
                  }

                  // 🔥 CREAR TURNO NORMAL
                  let start = date;
                  let end = date;

                  if (spot.isApartment) {
                    start = moment(date)
                      .hour(11)
                      .minute(0)
                      .second(0)
                      .toDate();

                    end = moment(date)
                      .hour(15)
                      .minute(0)
                      .second(0)
                      .toDate();
                  } else {
                    start = moment(date)
                      .hour(8)
                      .minute(0)
                      .second(0)
                      .toDate();

                    end = moment(date)
                      .hour(12)
                      .minute(0)
                      .second(0)
                      .toDate();
                  }

                  onAddShift(spot, start, end);
                }}

                onEditShift={(shiftId) => {
                  const shift = shifts.find(s => s.id === shiftId);
                  if (shift) onEditShift(shift);
                }}
                onDeleteShift={(shiftId) => {
                  const shift = shifts.find(s => s.id === shiftId);
                  if (shift) onDeleteShift(shift);
                }}
                onCopyShift={(shiftId) => {
                  const shift = shifts.find(s => s.id === shiftId);
                  if (shift) onCopyShift(shift);
                }}
              />
            </div>
           
          </div>
        </div>
        
      </>
    );

  }
);

export default ShiftRosterGrid;
